import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Locale, getTranslation, detectBrowserLocale, rtlLocales, localeNames } from "@/lib/translations";

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocale] = useState<Locale | null>(() => {
    if (typeof location === "undefined") return null;

    // load locale from ?locale=<code> when available
    const params = new URLSearchParams(location.search);
    const locale = params.get("locale");

    if (locale && Object.keys(localeNames).includes(locale)) {
      return locale as Locale;
    }

    const stored = localStorage.getItem("locale") as Locale | null;
    return stored || detectBrowserLocale();
  });
  const isRTL = locale ? rtlLocales.includes(locale) : false;

  useEffect(() => {
    if (!locale) return;
    localStorage.setItem("locale", locale);
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = locale;
  }, [locale, isRTL]);

  const t = (key: string): string => getTranslation(locale ?? "en", key);

  if (!locale) return null;

  return <LanguageContext.Provider value={{ locale, setLocale, t, isRTL }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
